import { Route } from '@angular/router';
import { Layout } from './ui/layout/layout';
import { authGuard } from './auth/auth.guard';
import { DashboardsLanding } from './features/dashboards-landing/dashboards-landing';
import { unsavedChangesGuard } from './shared/guards/unsaved-changes/unsaved-changes.guard';
import { ProjectSelection } from './features/project-selection/project-selection';
import { ProjectGuard } from './features/project-guard';
import { OverviewsLanding } from './features/overview-landing/overviews-landing';
import { permissionGuard } from './shared/guards/permission-guard/permission.guard';
import { Permissions } from '@anessa/shared-models';
import { urlIdGuard } from './shared/guards/url-id/url-id.guard';
import { SectionConfigApiService } from './features/data-management/section-config-api.service';
import { DashboardService } from './features/dashboard/services/dashboard/dashboard.service';
import { ReportService } from './features/report-creation/services/report.service';

export const appRoutes: Route[] = [
  {
    path: '',
    component: Layout,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        component: ProjectSelection,
      },
      {
        path: 'overview',
        canActivate: [permissionGuard],
        canActivateChild: [ProjectGuard],
        data: { permission: Permissions.VIEW_SHARED_DASHBOARDS },
        children: [
          {
            path: '',
            component: OverviewsLanding,
          },
          {
            path: ':id',
            canActivate: [urlIdGuard],
            data: {
              urlIdValidation: {
                param: 'id',
                validationService: DashboardService,
                redirectTo: '/overview',
              },
            },
            loadComponent: () =>
              import('./features/overview-page/overview-page').then(
                (m) => m.OverviewPage,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        canActivateChild: [ProjectGuard],
        data: { permission: Permissions.VIEW_INDIVIDUAL_DASHBOARDS },
        children: [
          {
            path: '',
            component: DashboardsLanding,
          },
          {
            path: ':id',
            canActivate: [urlIdGuard],
            data: {
              urlIdValidation: {
                param: 'id',
                validationService: DashboardService,
                redirectTo: '/dashboard',
              },
            },
            loadComponent: () =>
              import('./features/dashboards-page/dashboards-page').then(
                (m) => m.DashboardsPage,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: 'user-access-management',
        canActivate: [permissionGuard],
        data: { permission: Permissions.GLOBAL_ADMIN },
        loadComponent: () =>
          import(
            './features/permissions-management/user-access-management/user-access-management'
          ).then((m) => m.UserAccessManagement),
      },
      {
        path: 'role-permissions-management',
        canActivate: [permissionGuard],
        data: { permission: Permissions.GLOBAL_ADMIN },
        loadComponent: () =>
          import(
            './features/permissions-management/role-permission-management/role-permission-management'
          ).then((m) => m.RolePermissionManagement),
      },
      {
        path: 'insights',
        canActivate: [ProjectGuard, permissionGuard],
        data: { permission: Permissions.VIEW_METRICS },
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/metrics-manager/metrics-manager.component').then(
            (m) => m.MetricsManager,
          ),
      },
      {
        path: 'flare-analysis',
        canActivate: [ProjectGuard, permissionGuard],
        data: { permission: Permissions.VIEW_FLARE_ANALYSIS },
        loadComponent: () =>
          import('./features/flare-analysis/flare-analysis-page').then(
            (m) => m.FlareAnalysisPage,
          ),
      },

      {
        path: 'data-management',
        canActivate: [permissionGuard],
        canActivateChild: [ProjectGuard],
        data: { permission: Permissions.VIEW_MANAGED_DATA },
        children: [
          {
            path: 'weighbridge/data',
            canActivate: [permissionGuard],
            data: { permission: Permissions.VIEW_WEIGHBRIDGE },
            loadComponent: () =>
              import(
                './features/weighbridge/weighbridge-data/weighbridge-data-page'
              ).then((m) => m.WeighbridgeDataPage),
          },
          {
            path: 'weighbridge/data/:id',
            canActivate: [permissionGuard],
            data: { permission: Permissions.VIEW_WEIGHBRIDGE },
            loadComponent: () =>
              import(
                './features/weighbridge/weighbridge-data-detail/weighbridge-data-detail'
              ).then((m) => m.WeighbridgeDataDetail),
          },
          {
            path: 'weighbridge/mapping',
            canActivate: [permissionGuard],
            data: { permission: Permissions.VIEW_WEIGHBRIDGE },
            loadComponent: () =>
              import(
                './features/data-management/weighbridge-stub/weighbridge-stub'
              ).then((m) => m.WeighbridgeStub),
          },
          {
            path: ':section',
            canActivate: [urlIdGuard],
            data: {
              urlIdValidation: {
                param: 'section',
                validationService: SectionConfigApiService,
                redirectTo: '/data-management',
              },
            },
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./features/data-management/dynamic-section-page').then(
                (m) => m.DynamicSectionPage,
              ),
          },
          { path: '', redirectTo: 'clients', pathMatch: 'full' },
        ],
      },
      {
        path: 'timeseries-data',
        canActivate: [ProjectGuard, permissionGuard],
        data: { permission: Permissions.VIEW_TIMESERIES },
        children: [
          {
            path: ':tableId',
            loadComponent: () =>
              import(
                './features/timeseries-data/timeseries-data-page/timeseries-data-page'
              ).then((m) => m.TimeSeriesDataPage),
          },
          {
            path: '',
            loadComponent: () =>
              import(
                './features/timeseries-data/timeseries-data-page/timeseries-data-page'
              ).then((m) => m.TimeSeriesDataPage),
          },
        ],
      },
      {
        path: 'sensor-meta-editor',
        canActivate: [permissionGuard],
        data: { permission: Permissions.VIEW_SENSOR_META_EDITOR },
        loadComponent: () =>
          import('./features/sensor-meta-editor/sensor-meta-editor').then(
            (m) => m.SensorMetaEditor,
          ),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'metrics-status',
        canActivate: [ProjectGuard, permissionGuard],
        data: { permission: Permissions.RECOMPUTE_METRICS },
        loadComponent: () =>
          import('./features/metrics-status/metrics-status-page').then(
            (m) => m.MetricsStatusPage,
          ),
      },
      {
        path: 'plants',
        loadComponent: () =>
          import('./features/project-selection/project-selection').then(
            (m) => m.ProjectSelection,
          ),
      },
      {
        path: 'reports',
        canActivate: [ProjectGuard],
        children: [
          {
            path: '',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./features/report-creation/report-creation').then(
                (m) => m.ReportCreation,
              ),
          },
          {
            path: ':reportId',
            canActivate: [urlIdGuard],
            data: {
              urlIdValidation: {
                param: 'reportId',
                validationService: ReportService,
                redirectTo: '/reports',
              },
            },
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./features/report-creation/report-creation').then(
                (m) => m.ReportCreation,
              ),
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: '/', pathMatch: 'full' },
];
